import { useCallback, useMemo, useState } from "react";

// Client-side per-column sort for the small, fully-loaded tables (the org page's
// users + memexes lists — every row is already in hand, so there's no need for a
// server round-trip like the paginated overview table uses). The caller supplies
// an `accessors` map (defined as a stable module const) returning a comparable
// value per column; nulls always sort last. Mirrors useTableQuery's toggle
// semantics so the same <Th> header component drives both.
export function useClientSort<T>(
  rows: T[],
  accessors: Record<string, (row: T) => string | number | null>,
  defaultSort: string,
  defaultOrder: "asc" | "desc" = "desc",
) {
  const [sort, setSort] = useState(defaultSort);
  const [order, setOrder] = useState<"asc" | "desc">(defaultOrder);

  const toggleSort = useCallback((key: string) => {
    setSort((cur) => {
      if (cur === key) {
        setOrder((o) => (o === "asc" ? "desc" : "asc"));
        return cur;
      }
      setOrder("desc");
      return key;
    });
  }, []);

  const sorted = useMemo(() => {
    const acc = accessors[sort];
    if (!acc) return rows;
    return [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1; // nulls last regardless of direction
      if (vb === null) return -1;
      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb));
      return order === "asc" ? cmp : -cmp;
    });
  }, [rows, accessors, sort, order]);

  return { sorted, sort, order, toggleSort };
}
